import type React from "react"
import { Col, Row } from "reactstrap"
import { Line, LineChart, Bar, BarChart, Area, AreaChart, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts"
import type { DashboardChartsData } from "./types/dashboard"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface MiniChartsProps {
  chartsData: DashboardChartsData | null
  loading: boolean
}

const MiniCharts: React.FC<MiniChartsProps> = ({ chartsData, loading }) => {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-3 py-2 border-0 rounded shadow-sm" style={{ fontSize: "0.8rem" }}>
          <p className="text-muted mb-1">{new Date(label).toLocaleDateString()}</p>
          <p className="mb-0 fw-bold" style={{ color: payload[0].color || "#4f46e5" }}>
            {payload[0].value}
          </p>
        </div>
      )
    }
    return null
  }

  if (loading) {
    return (
      <Row>
        {Array.from({ length: 3 }).map((_, index) => (
          <Col xl="4" lg="6" md="12" key={index} className="mb-4">
            <div className="db-chart-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="db-skeleton" style={{ height: "18px", width: "55%" }}></div>
                <div className="db-skeleton" style={{ width: "30px", height: "30px", borderRadius: "8px" }}></div>
              </div>
              <div className="db-skeleton" style={{ height: "200px", width: "100%" }}></div>
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  const ticketData = chartsData?.ticketTrends.slice(-7) || []
  const vehicleData = chartsData?.vehicleTrends.slice(-7) || []
  const badgeData = chartsData?.badgeVerifications.slice(-7) || []

  const totalTicketSales = ticketData.reduce((sum, item) => sum + item.newBought, 0)
  const totalVehicleEntries = vehicleData.reduce((sum, item) => sum + item.entered, 0)
  const totalVerifications = badgeData.reduce((sum, item) => sum + item.verifications, 0)

  return (
    <Row>
      <Col xl="4" lg="6" md="12" className="mb-4">
        <div className="db-chart-card h-100">
          <div className="db-chart-head mb-2">
            <div>
              <div className="db-section-eyebrow mb-1">Daily Ticket Sales</div>
              <div className="db-chart-sub">Last 7 days</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="db-chart-total">
                <div className="db-chart-total-value db-text-indigo">{totalTicketSales}</div>
                <div className="db-chart-total-label">Total</div>
              </div>
              <div className="db-kpi-chip db-accent-indigo" style={{ width: 34, height: 34 }}>
                <i className="fa fa-shopping-cart" style={{ fontSize: "0.85rem" }}></i>
              </div>
            </div>
          </div>
          <div style={{ height: "190px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ticketData}>
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(79,70,229,0.06)" }} />
                <Bar dataKey="newBought" fill="#4f46e5" radius={[4, 4, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      <Col xl="4" lg="6" md="12" className="mb-4">
        <div className="db-chart-card h-100">
          <div className="db-chart-head mb-2">
            <div>
              <div className="db-section-eyebrow mb-1">Vehicle Entries</div>
              <div className="db-chart-sub">Last 7 days</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="db-chart-total">
                <div className="db-chart-total-value db-text-emerald">{totalVehicleEntries}</div>
                <div className="db-chart-total-label">Total</div>
              </div>
              <div className="db-kpi-chip db-accent-emerald" style={{ width: 34, height: 34 }}>
                <i className="fa fa-truck" style={{ fontSize: "0.85rem" }}></i>
              </div>
            </div>
          </div>
          <div style={{ height: "190px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={vehicleData}>
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="entered" stroke="#059669" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      <Col xl="4" lg="6" md="12" className="mb-4">
        <div className="db-chart-card h-100">
          <div className="db-chart-head mb-2">
            <div>
              <div className="db-section-eyebrow mb-1">Badge Verifications</div>
              <div className="db-chart-sub">Last 7 days</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="db-chart-total">
                <div className="db-chart-total-value db-text-cyan">{totalVerifications}</div>
                <div className="db-chart-total-label">Total</div>
              </div>
              <div className="db-kpi-chip db-accent-cyan" style={{ width: 34, height: 34 }}>
                <i className="fa fa-shield" style={{ fontSize: "0.85rem" }}></i>
              </div>
            </div>
          </div>
          <div style={{ height: "190px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={badgeData}>
                <defs>
                  <linearGradient id="miniBadgeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0891b2" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0891b2" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="verifications" stroke="#0891b2" strokeWidth={2.5} fill="url(#miniBadgeGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>
    </Row>
  )
}

export default MiniCharts
